import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { ItemListOption } from '@/contexts/item-context-types';
import { List } from '@/components/list';
import { ListOption } from '@/components/list-item';
import { PrinterSelectList } from '@/components/printer-select-list';
import { SCREEN_PADDING } from '@/styles';
import { StyleSheet, useWindowDimensions } from 'react-native';
import { SvgProps } from 'react-native-svg';
import { TagPrintingSummary } from '@/components/tag-printing-summary';
import { TagPrintingSummaryV2 } from '@/components/tag-printing-summary-v2';
import { TagPrintingTemplateSelection } from '@/components/tag-printing-template-selection-v2';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { VariantPackSelection } from '@/components/variant-pack-selection';

export enum ItemMenuOperationsRoute {
  CreateSubscription = 'CREATE_SUBSCRIPTION',
  CreditRequest = 'CREDIT_REQUEST',
  SelectDevice = 'SELECT_DEVICE',
  SelectOperation = 'SELECT_OPERATION',
  TagPrinting = 'TAG_PRINTING',
  TagPrintingTemplateSelection = 'TAG_PRINTING_TEMPLATE_SELECTION',
  TagPrintingVariantPackSelection = 'TAG_PRINTING_VARIANT_PACK_SELECTION',
}

export type ItemMenuOperationOption = ListOption<{
  Icon: React.FC<SvgProps>;
}>;

interface Props {
  onClose: () => void;
  onNavigateToCreateSubscriptionScreen: (variantPackUUID: string) => void;
  onNavigateToCreditRequestScreen: () => void;
  operations: ItemMenuOperationOption[];
  selectedItem: ItemListOption;
  variantPackUUIDs: string[];
}

export const ItemMenuOperationsActionSheet: React.FC<Props> = ({
  onClose,
  onNavigateToCreateSubscriptionScreen,
  onNavigateToCreditRequestScreen,
  operations,
  selectedItem,
}) => {
  const windowDimensions = useWindowDimensions();
  const actionSheetMaxHeight = Math.floor(windowDimensions.height * 0.9);

  /**
   * -------------- State Management for Tag Printing --------------
   **/
  const variantPacks = selectedItem.metadata?.skus || [];

  const autoSelectedFirstVariantPackUUID =
    variantPacks.length === 1 ? variantPacks[0].variantPackUUID : null;

  const genericTagPrintingEnabled = useDynamicConfigValue(
    'tag_printing_config',
  )?.generic_tag_printing_enabled;

  const [selectedVariantPackUUID, setSelectedVariantPackUUID] = useState<
    string | null
  >(autoSelectedFirstVariantPackUUID);

  const [selectedTemplateUUID, setSelectedTemplateUUID] = useState<
    string | null
  >(null);

  const [customPriceInMicros, setCustomPriceInMicros] = useState<number | null>(
    null,
  );

  /**
   * ---------- End of State Management for Tag Printing ----------
   **/

  return (
    <CombinedActionSheet
      animateTransitions
      contentStyle={[styles.base, { maxHeight: actionSheetMaxHeight }]}
      hasStickyHeader={false}
      initialRoute={ItemMenuOperationsRoute.SelectOperation}
      onClose={onClose}
      onRouteChange={(nextKey, previousKey) => {
        if (
          nextKey === ItemMenuOperationsRoute.TagPrintingVariantPackSelection &&
          previousKey === ItemMenuOperationsRoute.TagPrintingTemplateSelection
        ) {
          setSelectedVariantPackUUID(null);
          setSelectedTemplateUUID(null);
          setCustomPriceInMicros(null);
        }
      }}
      renderScene={({ jumpTo, routeKey }) => {
        switch (routeKey) {
          case ItemMenuOperationsRoute.SelectDevice:
            return (
              <PrinterSelectList
                onSelect={() => {
                  jumpTo(ItemMenuOperationsRoute.TagPrinting);
                }}
              />
            );
          case ItemMenuOperationsRoute.SelectOperation:
            return (
              <List
                data={operations}
                onSelect={(item) => {
                  if (item.value === ItemMenuOperationsRoute.CreditRequest) {
                    onNavigateToCreditRequestScreen();
                  } else if (
                    item.value === ItemMenuOperationsRoute.CreateSubscription &&
                    variantPacks.length === 1
                  ) {
                    onNavigateToCreateSubscriptionScreen(
                      variantPacks[0].variantPackUUID,
                    );
                  } else {
                    jumpTo(item.value as ItemMenuOperationsRoute);
                  }
                }}
                renderLeftComponent={(item) => {
                  const Icon = item.metadata!.Icon;
                  return <Icon />;
                }}
              />
            );
          case ItemMenuOperationsRoute.CreateSubscription:
            return (
              <VariantPackSelection
                onSelectVariantPackUUID={(variantPackUUID) => {
                  onNavigateToCreateSubscriptionScreen(variantPackUUID);
                }}
                variantPacks={variantPacks}
              />
            );
          case ItemMenuOperationsRoute.TagPrinting:
            return genericTagPrintingEnabled ? (
              <TagPrintingSummaryV2
                goBack={() => {
                  jumpTo(ItemMenuOperationsRoute.SelectOperation);
                }}
                goForward={() => {
                  jumpTo(
                    selectedVariantPackUUID
                      ? ItemMenuOperationsRoute.TagPrintingTemplateSelection
                      : ItemMenuOperationsRoute.TagPrintingVariantPackSelection,
                  );
                }}
              />
            ) : (
              <TagPrintingSummary
                goBack={() => {
                  jumpTo(ItemMenuOperationsRoute.SelectOperation);
                }}
                onClose={onClose}
                selectedItem={selectedItem}
                selectPrinter={() => {
                  jumpTo(ItemMenuOperationsRoute.SelectDevice);
                }}
              />
            );
          case ItemMenuOperationsRoute.TagPrintingVariantPackSelection:
            if (selectedVariantPackUUID) {
              setSelectedVariantPackUUID(selectedVariantPackUUID);
              jumpTo(ItemMenuOperationsRoute.TagPrintingTemplateSelection);
            } else {
              return (
                <VariantPackSelection
                  onSelectVariantPackUUID={(variantPackUUID) => {
                    setSelectedVariantPackUUID(variantPackUUID);
                    jumpTo(
                      ItemMenuOperationsRoute.TagPrintingTemplateSelection,
                    );
                  }}
                  variantPacks={variantPacks}
                />
              );
            }
          case ItemMenuOperationsRoute.TagPrintingTemplateSelection:
            return (
              <TagPrintingTemplateSelection
                customPriceInMicros={customPriceInMicros}
                selectedTemplateUUID={selectedTemplateUUID}
                selectedVariantPackUUID={selectedVariantPackUUID!}
                selectPrinter={() => {
                  jumpTo(ItemMenuOperationsRoute.SelectDevice);
                }}
                setCustomPriceInMicros={setCustomPriceInMicros}
                setSelectedTemplateUUID={setSelectedTemplateUUID}
              />
            );
          default:
            return null;
        }
      }}
      routes={{
        [ItemMenuOperationsRoute.CreditRequest]: {
          previousKey: ItemMenuOperationsRoute.SelectOperation,
        },
        [ItemMenuOperationsRoute.CreateSubscription]: {
          previousKey: ItemMenuOperationsRoute.SelectOperation,
          showSmallLayoutBackButton: true,
        },
        [ItemMenuOperationsRoute.SelectDevice]: {
          previousKey: genericTagPrintingEnabled
            ? ItemMenuOperationsRoute.TagPrintingVariantPackSelection
            : ItemMenuOperationsRoute.TagPrinting,
          showSmallLayoutBackButton: true,
          showSmallLayoutTitle: true,
          title: i18n.t('select_printer'),
        },
        [ItemMenuOperationsRoute.SelectOperation]: {
          previousKey: null,
        },
        [ItemMenuOperationsRoute.TagPrinting]: {
          previousKey: ItemMenuOperationsRoute.SelectOperation,
        },
        [ItemMenuOperationsRoute.TagPrintingVariantPackSelection]: {
          previousKey: ItemMenuOperationsRoute.SelectOperation,
          showSmallLayoutBackButton: true,
        },
        [ItemMenuOperationsRoute.TagPrintingTemplateSelection]: {
          previousKey:
            variantPacks.length === 1
              ? ItemMenuOperationsRoute.SelectOperation
              : ItemMenuOperationsRoute.TagPrintingVariantPackSelection,
          showSmallLayoutBackButton: true,
        },
      }}
      titleStyle={styles.content}
    />
  );
};

const styles = StyleSheet.create({
  base: {
    paddingBottom: 48,
    paddingHorizontal: 0,
    paddingTop: 24,
  },
  content: {
    paddingHorizontal: SCREEN_PADDING,
  },
});
